<script>
import Layout from "../../layouts/main.vue";
import PageHeader from "@/components/page-header.vue";
import appConfig from "@/app.config.json";
import axios from "axios";
import errorHandling from "../../../mixins/errorHandling";

/**
 * Advanced table component
 */
export default {
  page: {
    title: "Customers",
    meta: [{ name: "description", content: appConfig.description }],
  },
  mixins: [errorHandling],
  components: { Layout, PageHeader },
  data() {
    return {
      customers: [],
      agentID: localStorage.getItem("id"),
      title: "Customers",
      items: [
        {
          text: "Agent",
        },
        {
          text: "Customers",
          active: true,
        },
      ],
      totalRows: 1,
      currentPage: 1,
      perPage: 20,
      pageOptions: [20, 25, 50, 100],
      filter: null,
      filterOn: [],
      sortBy: "age",
      sortDesc: false,
      fields: [
        { key: "displayName", sortable: true },
        { key: "accountNo", sortable: true },
        { key: "mobileNo", sortable: true },
        { key: "gender.name", sortable: true, label: "Gender" },
        { key: "officeName", sortable: true },
        { key: "id", label: "View Loans" },
      ],
      loading: false,
      filterStatus: "active",
    };
  },
  computed: {
    /**
     * Total no. of records
     */
    rows() {
      return this.customers.length;
    },
    filteredCustomers() {
      if (this.filterStatus === "active") {
        return this.customers.filter((customer) => customer.active === true);
      } else if (this.filterStatus === "inactive") {
        return this.customers.filter((customer) => customer.active === false);
      } else {
        return this.customers;
      }
    },
  },
  mounted() {
    // Set the initial number of items
    this.totalRows = this.items.length;
    this.getCustomers();
  },
  methods: {
    /**
     * Search the table data with search input
     */
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    showActiveCustomers() {
      this.filterStatus = "active";
    },
    showInactiveCustomers() {
      this.filterStatus = "inactive";
    },

    showAllCustomers() {
      this.filterStatus = null;
    },

    async getCustomers() {
      this.loading = true;
      try {
        const response = await axios.get(
          `${
            process.env.VUE_APP_BASE_URL
          }/agent/get-customers/${localStorage.getItem("id")}`
        );
        if (response.status === 200 || response.status === 201) {
          this.customers = response.data.data.pageItems;
          this.loading = false;
        } else {
          this.loading = false;
          this.throwError();
        }
      } catch (error) {
        this.loading = false;
        this.handleError(error.response.data.message);
      }
    },
    getCustomer(item) {
      this.$router.push(`/agent/verify-payment/${item.id}`);
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row" v-if="loading">
      <b-spinner class="m-2 m-auto" variant="primary" role="status"></b-spinner>
    </div>
    <div class="row" v-else>
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title">Customer Table</h4>
            <div class="mt-4 mb-4 row text-center">
              <div class="col-sm-12 col-md-4 mb-1">
                <b-button
                  variant="secondary"
                  @click.prevent="showInactiveCustomers"
                >
                  <i class="ri-user-unfollow-line align-middle mr-2"></i> Show
                  Inactive Customers
                </b-button>
              </div>
              <div class="col-sm-12 col-md-4 mb-1">
                <b-button
                  variant="secondary"
                  @click.prevent="showActiveCustomers"
                >
                  <i class="ri-user-follow-line align-middle mr-2"></i> Show
                  Active Customers
                </b-button>
              </div>

              <div class="col-sm-12 col-md-4 mb-1">
                <b-button variant="secondary" @click.prevent="showAllCustomers">
                  <i class="ri-team-line align-middle mr-2"></i> Show All
                  Customers
                </b-button>
              </div>
            </div>
            <div class="row mt-4">
              <div class="col-sm-12 col-md-6">
                <div id="tickets-table_length" class="dataTables_length">
                  <label class="d-inline-flex align-items-center">
                    Show&nbsp;
                    <b-form-select
                      v-model="perPage"
                      size="sm"
                      :options="pageOptions"
                    ></b-form-select
                    >&nbsp;entries
                  </label>
                </div>
              </div>
              <!-- Search -->
              <div class="col-sm-12 col-md-6">
                <div
                  id="tickets-table_filter"
                  class="dataTables_filter text-md-right"
                >
                  <label class="d-inline-flex align-items-center">
                    Search:
                    <b-form-input
                      v-model="filter"
                      type="search"
                      placeholder="Search..."
                      class="form-control form-control-sm ml-2"
                    ></b-form-input>
                  </label>
                </div>
              </div>
              <!-- End search -->
            </div>
            <!-- Table -->
            <div class="table-responsive row mb-0 pointer-cursor-table">
              <b-table
                @row-clicked="getCustomer"
                :items="filteredCustomers"
                :fields="fields"
                responsive="sm"
                stacked="sm"
                :per-page="perPage"
                :current-page="currentPage"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :filter="filter"
                hover
                :filter-included-fields="filterOn"
                @filtered="onFiltered"
              >
                <template v-slot:cell(id)="row">
                  <div class="row button-items">
                    <div class="">
                      <router-link
                        :to="`/agent/customer/transactions/${row.value}`"
                      >
                        <b-button variant="warning">
                          <i class="ri-funds-line align-middle mr-2"></i> View
                          Transactions
                        </b-button>
                      </router-link>
                    </div>

                    <div class="">
                      <router-link :to="`/agent/verify-payment/${row.value}`">
                        <b-button variant="success">
                          <i class="ri-eye-line align-middle mr-2"></i> View
                          Accounts
                        </b-button>
                      </router-link>
                    </div>
                  </div>
                </template>
              </b-table>
            </div>
            <div class="row">
              <div class="col">
                <div
                  class="dataTables_paginate paging_simple_numbers float-right"
                >
                  <ul class="pagination pagination-rounded mb-0">
                    <!-- pagination -->
                    <b-pagination
                      v-model="currentPage"
                      :total-rows="rows"
                      :per-page="perPage"
                    ></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
<style scoped>
.pointer-cursor-table tbody tr:hover {
  cursor: pointer;
}
</style>
